import { useAppSelector } from "@/redux/features/hooks";
import { useGetRestaurantDailyDishesQuery } from "@/redux/services/dailyDishes/dailyDishes.api";
import { DailyDish } from "@/redux/services/dailyDishes/dailyDishes.type";
import DailyDishFormDrawer from "@/components/daily-dishes/DailyDishFormDrawer";
import DailyDishDetailsDrawer from "@/components/daily-dishes/DailyDishDetailsDrawer";
import DailyDishCard, {
  DailyDishCardSkeleton,
} from "@/components/daily-dishes/DailyDishCard";
import { ButtonAdd } from "@/components/ui/button";
import { useState } from "react";
import { ChefHat } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";

const DailyDishes = () => {
  const { currentRestaurant } = useAppSelector((state) => state.user);
  const { canManage } = usePermissions();
  const canManageMenu = canManage("menu");
  const { data, isLoading } = useGetRestaurantDailyDishesQuery({
    restaurantId: currentRestaurant?.id,
    limit: 100,
  });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DailyDish | undefined>();
  const [detailsId, setDetailsId] = useState<string | null>(null);

  const handleAdd = () => {
    setEditTarget(undefined);
    setIsFormOpen(true);
  };

  const handleEditFromDetails = (dish: DailyDish) => {
    setDetailsId(null);
    setEditTarget(dish);
    setIsFormOpen(true);
  };

  const closeFormDrawer = () => {
    setIsFormOpen(false);
    setEditTarget(undefined);
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {[...Array(5)].map((_, i) => (
          <DailyDishCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  const dishes = data?.results ?? [];

  return (
    <>
      {canManageMenu && (
        <div className="flex justify-end mb-6">
          <ButtonAdd label="Nouveau plat du jour" onClick={handleAdd} />
        </div>
      )}

      {dishes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <ChefHat className="h-12 w-12 text-muted-foreground opacity-30 mb-3" />
          <p className="text-muted-foreground text-lg mb-2">Aucun plat du jour</p>
          <p className="text-muted-foreground text-sm">
            Cliquez sur "Nouveau plat du jour" pour commencer
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {dishes.map((dish) => (
            <DailyDishCard
              key={dish.id}
              dailyDish={dish}
              openDailyDishModal={(d) => setDetailsId(d.id ?? null)}
            />
          ))}
        </div>
      )}

      <DailyDishDetailsDrawer
        dailyDishId={detailsId}
        isOpen={!!detailsId}
        onClose={() => setDetailsId(null)}
        onEdit={handleEditFromDetails}
      />

      <DailyDishFormDrawer
        isOpen={isFormOpen}
        onClose={closeFormDrawer}
        dailyDish={editTarget}
      />
    </>
  );
};

export default DailyDishes;
